<script setup lang="ts">
import { type Component, computed, toRefs } from 'vue';

import usersRepository from '@/api/usersRepository';
import IconBrandwatchLogo from '@/components/icons/IconBrandwatchLogo.vue';
import IconCampaigns from '@/components/icons/IconCampaigns.vue';
import IconDiscover from '@/components/icons/IconDiscover.vue';
import IconHome from '@/components/icons/IconHome.vue';
import IconInbox from '@/components/icons/IconInbox.vue';
import IconInfluencers from '@/components/icons/IconInfluencers.vue';
import IconLogout from '@/components/icons/IconLogout.vue';
import IconPayments from '@/components/icons/IconPayments.vue';
import IconSettings from '@/components/icons/IconSettings.vue';
import { useUserStore } from '@/stores/user';

const APP_NAME = import.meta.env.VITE_APP_NAME;
const ACCOUNTS_BASE_URL = import.meta.env.VITE_ACCOUNTS_BASE_URL;

const APP_ICONS: Record<string, Component> = {
  home: IconHome,
  discover: IconDiscover,
  influencers: IconInfluencers,
  campaigns: IconCampaigns,
  payments: IconPayments,
  networks: IconPayments,
  messages: IconInbox
};

const userStore = useUserStore();

const { currentUser, isAppInitialized } = toRefs(userStore);

const availableApplications = computed(() => {
  return currentUser.value?.availableApplications?.map((app) => ({
    name: app.name,
    humanName: app.humanName,
    url: app.url,
    icon: APP_ICONS[app.name]
  }));
});

const isDemoUser = computed(() => currentUser.value?.demo);

const logout = async () => {
  await usersRepository.logout();
};
</script>

<template>
  <aside>
    <div class="logo">
      <IconBrandwatchLogo />
    </div>
    <nav>
      <div class="nav-top">
        <a
          v-for="app in availableApplications"
          :key="app.name"
          class="navbar-link"
          :class="{ active: app.name === APP_NAME }"
          :href="app.url"
          :id="`navbar-app-${app.name}`"
        >
          <component
            :is="app.icon"
            class="app-icon"
          />
          <span class="app-name">{{ app.humanName }}</span>
        </a>
      </div>
      <div v-if="isAppInitialized"
        class="nav-bottom"
      >
        <a
          v-if="!isDemoUser"
          class="navbar-link"
          :href="ACCOUNTS_BASE_URL"
          id="navbar-app-settings"
        >
          <IconSettings class="app-icon" />
          <span class="app-name">{{ $t('switchbar.settings') }}</span>
        </a>
        <span
          class="navbar-link"
          @click="logout"
          id="navbar-app-logout"
        >
          <IconLogout class="app-icon" />
          <span class="app-name">{{ $t('switchbar.logout') }}</span>
        </span>
      </div>
    </nav>
  </aside>
</template>

<style lang="scss" scoped>
aside {
  background: var(--gray);
  width: 80px;
  min-height: 100dvh;
  border-right: 1px solid var(--gray-dark);
  display: flex;
  flex-direction: column;
}

.logo {
  width: 80px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

nav {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
}

.navbar-link {
  font-size: 14px;
  line-height: 17px;
  width: 80px;
  min-height: 75px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;

  .app-name {
    font-size: 11px;
    text-transform: capitalize;
    color: var(--text-color);
    font-weight: bold;
    text-align: center;
  }

  &.active,
  &:hover {
    .app-name {
      color: var(--primary);
    }

    :deep(path) {
      fill: var(--primary);
    }
  }

  &.active {
    background-color: var(--blue-light);

    :deep(path) {
      fill: var(--primary);
    }
  }
}
</style>
